import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import {
  colorAccent,
  colorBackground,
  colorBackgroundInfo,
  colorBorderError,
  colorBorderInfo,
  colorSurface,
  colorSurfaceError,
  colorTextError,
  colorTextInfo,
  colorTextMuted,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { useCreateAnonymousBooking } from "src/data/booking/queries/booking";
import {
  PublicDeal,
  useGetPublicDeals,
  useGetPublicRestaurant,
  useGetPublicRestaurantSchedule,
} from "src/data/clientBooking/queries/clientBooking";
import { SnackBar } from "src/design";
import RestaurantHeader from "./components/RestaurantHeader";
import DateSelection from "./components/DateSelection";
import TimeSelection from "./components/TimeSelection";
import GuestSelection from "./components/GuestSelection";
import BookingDetailsForm, {
  BookingDetailsValues,
} from "./components/BookingDetailsForm";
import { getSelectableDates, getTimeSlotsForDay } from "./utils";

// 0=date, 1=time, 2=guests, 3=details
type Step = 0 | 1 | 2 | 3;

const ClientBookingPage = () => {
  const { t } = useTranslation();
  const { restaurant_name = "", restaurant_city = "" } = useParams<{
    restaurant_name: string;
    restaurant_city: string;
  }>();

  const displayName = restaurant_name.replaceAll("-", " ");
  const displayCity = restaurant_city.replaceAll("-", " ");

  const [step, setStep] = useState<Step>(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [applicableDeals, setApplicableDeals] = useState<PublicDeal[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<PublicDeal | null>(null);
  const [guestCount, setGuestCount] = useState(2);
  const [bookingError, setBookingError] = useState(false);
  const [slotFull, setSlotFull] = useState(false);

  const {
    data: restaurant,
    isLoading: restaurantLoading,
    isError: restaurantError,
    error: restaurantErrorObj,
  } = useGetPublicRestaurant(displayName, displayCity);

  const { data: scheduleData, isLoading: scheduleLoading } =
    useGetPublicRestaurantSchedule(restaurant?.id);

  const { data: deals = [] } = useGetPublicDeals(restaurant?.id);

  const availableDates = useMemo(() => {
    if (!scheduleData) return [];
    return getSelectableDates(scheduleData.schedule, scheduleData.leaves);
  }, [scheduleData]);

  const specialDealDates = useMemo(
    () => deals.flatMap((d) => d.applicableDays),
    [deals],
  );

  const { morning: morningSlots, evening: eveningSlots } = useMemo(() => {
    if (!scheduleData || !selectedDate || !restaurant)
      return { morning: [], evening: [] };

    return getTimeSlotsForDay(
      scheduleData.schedule,
      selectedDate,
      restaurant.bookingDuration,
    );
  }, [scheduleData, selectedDate, restaurant]);

  const { mutate: createBooking, isPending } = useCreateAnonymousBooking(
    () => setIsSuccess(true),
    (err) => {
      // Ghost success: a blacklisted booker is shown the normal success screen
      // and no real booking is created, so they get no in-product signal that
      // they were singled out. Keys off the exact backend error string — we do
      // not fabricate a success for genuine failures. Slot-full and generic
      // errors are unchanged below.
      if (err.message === "This booker is not allowed at this restaurant") {
        setIsSuccess(true);
      } else if (err.message?.toLowerCase().includes("slot")) {
        setSlotFull(true);
      } else {
        setBookingError(true);
      }
    },
  );

  const handleSelectDate = (date: Dayjs) => {
    setSelectedDate(date);
    const dateStr = date.format("YYYY-MM-DD");
    setApplicableDeals(deals.filter((d) => d.applicableDays.includes(dateStr)));
    setSelectedDeal(null);
  };

  const handleConfirmDate = () => {
    setStep(1);
  };

  const handleSelectTime = (time: string, deal: PublicDeal | null) => {
    setSelectedTime(time);
    setSelectedDeal(deal);
    setStep(2);
  };

  const handleSubmitDetails = (values: BookingDetailsValues) => {
    if (!restaurant || !selectedDate || !selectedTime) return;

    const hour = selectedTime.split(":")[0];
    const minutes = selectedTime.split(":")[1];

    createBooking({
      restaurantId: restaurant.id,
      date: selectedDate
        .set("hour", Number(hour))
        .set("minute", Number(minutes))
        .toISOString(),
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      numberOfSeats: guestCount,
      country: "France",
      dealId: selectedDeal?.id ?? null,
      dealType: selectedDeal?.type ?? null,
      notes: values.notes,
    });
  };

  if (restaurantLoading || scheduleLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colorBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (restaurantError || !restaurant) {
    const isNotFound =
      restaurantErrorObj instanceof Error &&
      restaurantErrorObj.message === "NOT_FOUND";

    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colorBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            backgroundColor: isNotFound ? colorSurfaceError : colorSurface,
            border: `1px solid ${isNotFound ? colorBorderError : colorBorderError}`,
            borderRadius: radiusCard,
            boxShadow: shadowCard,
            p: 3,
            maxWidth: 400,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            textAlign: "center",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 40, color: colorTextError }} />
          <Typography variant="body1" color={colorTextError} fontWeight={500}>
            {t(
              isNotFound
                ? "clientBooking.restaurantNotFound"
                : "clientBooking.errorLoading",
            )}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isSuccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colorBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          px: 2,
          py: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: colorSurface,
            borderRadius: radiusCard,
            boxShadow: shadowCard,
            p: { xs: 3, sm: 4 },
            textAlign: "center",
            maxWidth: 400,
            width: "100%",
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 56, color: colorAccent, mb: 2 }}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {t("clientBooking.bookingSuccess")}
          </Typography>
          <Typography variant="body2" color={colorTextMuted}>
            {t("clientBooking.bookingSuccessMessage", {
              restaurant: restaurant.name,
            })}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colorBackground,
        width: "100%",
        pb: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "100%", sm: 560 },
          mx: "auto",
        }}
      >
        <RestaurantHeader restaurant={restaurant} />

        {false && (
          <Box
            sx={{
              backgroundColor: colorBackgroundInfo,
              border: `1px solid ${colorBorderInfo}`,
              borderRadius: radiusCard,
              p: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <EventBusyIcon sx={{ fontSize: 22, color: colorTextInfo, flexShrink: 0 }} />
            <Typography variant="body2" color={colorTextInfo} fontWeight={500}>
              {t("clientBooking.notAcceptingBookings")}
            </Typography>
          </Box>
        )}

        {true && step === 0 && (
          <DateSelection
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            onConfirmDate={handleConfirmDate}
            availableDates={availableDates}
            specialDealDates={specialDealDates}
            applicableDeals={applicableDeals}
          />
        )}

        {true && step === 1 && selectedDate && (
          <TimeSelection
            selectedDate={selectedDate}
            morningSlots={morningSlots}
            eveningSlots={eveningSlots}
            applicableDeals={applicableDeals}
            onSelectTime={handleSelectTime}
            onBack={() => setStep(0)}
          />
        )}

        {true && step === 2 && (
          <GuestSelection
            guestCount={guestCount}
            onChangeGuestCount={setGuestCount}
            onContinue={() => setStep(3)}
            onBack={() => setStep(1)}
            phone={restaurant.phone}
            maximumPartySize={restaurant.maximumPartySize ?? null}
          />
        )}

        {true && step === 3 && selectedDate && selectedTime && (
          <BookingDetailsForm
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            guestCount={guestCount}
            restaurantPhone={restaurant.phone}
            isPending={isPending}
            onSubmit={handleSubmitDetails}
            onBack={() => setStep(2)}
          />
        )}
      </Box>

      <SnackBar
        open={bookingError}
        onClose={() => setBookingError(false)}
        message={t("clientBooking.bookingError")}
        severity="error"
      />
      <SnackBar
        open={slotFull}
        onClose={() => setSlotFull(false)}
        message={t("booking.slotsFull")}
        severity="error"
      />
    </Box>
  );
};

export default ClientBookingPage;
